import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import {
  ArrowRight, Crown, Star, Sun, Sparkle, Hash, Certificate, BookOpen,
  CheckCircle, X, FilePdf, Clock, Shield, CaretDown, Phone, Envelope,
  Lightning, ShieldCheck, Globe, GraduationCap, Heart, CurrencyInr,
  Briefcase, House, HandPalm, UserFocus,
} from "@phosphor-icons/react";

/**
 * Public "Reports" tab on the home page — no login required to browse.
 * Inspired by astroarunpandit.org/the-premium-personalized-kundli/
 *
 * Sections:
 *   A. Hero
 *   B. Comparison table (Features × 7 Reports)
 *   C. 7 Report cards (each navigates to its dedicated landing page)
 *   D. Who is this for? (5 personas)
 *   E. What's inside (icons + descriptions)
 *   F. Why OccultKing
 *   G. How it works (3 steps)
 *   H. Testimonials
 *   I. FAQ accordion
 */

const REPORTS = [
  {
    key: "mahakundli",
    title: "Mahakundli Report",
    tagline: "Lifetime Vedic Blueprint",
    pages: "50+ pages",
    method: "Classical Parashari",
    description:
      "A lifetime astrological blueprint — Mahadasha, 12-house life predictions, Yogas, Doshas, Mangal Dosha analysis, remedies for every planet, written for YOUR exact chart in 16 sections.",
    inside: [
      "Lifetime Mahadasha Timeline",
      "12 House Predictions (Career, Love, Money…)",
      "Detailed Yoga & Dosha Analysis",
      "Gemstone + Mantra + Yantra Remedies",
    ],
    price: 527,
    strike: 999,
    icon: Crown,
    accent: "amber",
    bgClass: "from-amber-100 via-orange-50 to-yellow-50",
    iconClass: "text-amber-700",
    chipClass: "bg-amber-200 text-amber-900",
    to: "/mahakundli",
  },
  {
    key: "lalkitab",
    title: "Lal Kitaab Report",
    tagline: "Free Practical Remedies",
    pages: "30+ pages",
    method: "Pt. Roop Chand Joshi tradition",
    description:
      "The authentic Lal Kitaab tradition with 7 Karzas, Buland / Soya / Mandi planet states, ancestral debt analysis, and free practical totke — no expensive gems needed.",
    inside: [
      "7 Pitra Karzas (Ancestral Debt) Analysis",
      "Buland / Soya / Mandi Planet Status",
      "Free Practical Totke (Home Remedies)",
      "House-wise Predictions",
    ],
    price: 527,
    strike: 999,
    icon: BookOpen,
    accent: "red",
    bgClass: "from-red-100 via-rose-50 to-orange-50",
    iconClass: "text-red-700",
    chipClass: "bg-red-200 text-red-900",
    to: "/lalkitab",
  },
  {
    key: "bnn",
    title: "BNN Report",
    tagline: "Bhrigu Nandi Nadi",
    pages: "50+ pages",
    method: "R.G. Rao Nadi tradition",
    description:
      "Nadi-style predictive reading — Karaka conjunctions, Bhrigu Bindu, Jaimini Chara Karakas, and 12-month transit windows. No Dasha needed — pure Karaka-based prediction.",
    inside: [
      "Karaka Conjunctions & Predictions",
      "Bhrigu Bindu Time-bound Events",
      "Jaimini Chara Karakas (Atma, Amatya…)",
      "12-Month Transit Activation Map",
    ],
    price: 527,
    strike: 999,
    icon: Sparkle,
    accent: "emerald",
    bgClass: "from-emerald-100 via-green-50 to-amber-50",
    iconClass: "text-emerald-700",
    chipClass: "bg-emerald-200 text-emerald-900",
    to: "/bnn",
  },
  {
    key: "rajyog",
    title: "Raj Yog Report",
    tagline: "When Will You Rise?",
    pages: "50+ pages",
    method: "Classical Parashari",
    description:
      "Detects every Raj Yoga in your chart — Pancha Mahapurusha, Kendra-Trikona, Vipreet, Dhana, Gajakesari, Neecha Bhanga — and the exact dashas that activate them.",
    inside: [
      "30+ Classical Raj Yogas Detected",
      "Pancha Mahapurusha Yogas",
      "5-Year Activation Timeline",
      "Strengthening Remedies",
    ],
    price: 527,
    strike: 999,
    icon: Crown,
    accent: "sapphire",
    bgClass: "from-blue-100 via-indigo-50 to-sky-50",
    iconClass: "text-blue-700",
    chipClass: "bg-blue-200 text-blue-900",
    to: "/raj-yog",
  },
  {
    key: "lifereport",
    title: "Life Report",
    tagline: "Plain-Language Life Roadmap",
    pages: "25 pages",
    method: "Vedic + Practical Coaching",
    description:
      "A layman's life roadmap with 12-month + 5-year + 15-year forecast, and time-anchored daily, weekly, monthly and yearly remedies. Zero astrology jargon.",
    inside: [
      "12-Month + 5-Year + 15-Year Forecast",
      "Daily / Weekly / Monthly Remedies",
      "Career, Love, Money & Health Roadmap",
      "Written in plain conversational language",
    ],
    price: 527,
    strike: 999,
    icon: Sun,
    accent: "violet",
    bgClass: "from-violet-100 via-purple-50 to-fuchsia-50",
    iconClass: "text-violet-700",
    chipClass: "bg-violet-200 text-violet-900",
    to: "/life-report",
  },
  {
    key: "astrovastu",
    title: "Astro-Vastu Report",
    tagline: "Where Stars Meet Your Space",
    pages: "25+ pages",
    method: "KP-method + Directional Vastu",
    description:
      "Acharya Vikul's signature KP-method analysis fused with directional Vastu — pinpoints exactly which planet, zone and remedy applies to YOUR life concerns.",
    inside: [
      "KP-Significator Analysis",
      "16 Vastu Zones × Planetary Influence",
      "Concerns-driven Remedies",
      "Mantra + Yantra + Direction Remedies",
    ],
    price: 527,
    strike: 999,
    icon: Star,
    accent: "purple",
    bgClass: "from-purple-100 via-violet-50 to-indigo-50",
    iconClass: "text-purple-700",
    chipClass: "bg-purple-200 text-purple-900",
    to: "/astro-vastu-report",
  },
  {
    key: "numerology",
    title: "Numerology Report",
    tagline: "Lo Shu Grid + Forecast",
    pages: "20+ pages",
    method: "Lo Shu Grid + Pythagorean",
    description:
      "Driver number, Conductor number, missing-number remedies, name vibration analysis, crystal recommendations, 3-year forecast and lucky number / colour / day combinations.",
    inside: [
      "Lo Shu Grid Analysis",
      "Driver + Conductor + Bhagyank Decode",
      "3-Year Personal Year Forecast",
      "Missing Number Crystal Remedies",
    ],
    price: 527,
    strike: 999,
    icon: Hash,
    accent: "violet",
    bgClass: "from-fuchsia-100 via-pink-50 to-rose-50",
    iconClass: "text-fuchsia-700",
    chipClass: "bg-fuchsia-200 text-fuchsia-900",
    to: "/numerology",
  },
  {
    key: "palmistry",
    title: "Palmistry Report",
    tagline: "Both Hands Read by AI",
    pages: "20+ pages",
    method: "Hast Rekha Shastra",
    description:
      "Upload photos of both palms for a detailed premium reading — major & minor lines, mounts, special signs, personality, life timeline, predictions and remedies, comparing your inherited potential with what you've built.",
    inside: [
      "Both Hands Compared (potential vs built)",
      "All Major & Minor Lines + Mounts",
      "Life Timeline & Predictions",
      "Gemstone / Rudraksha / Yantra Remedies",
    ],
    price: 527,
    strike: 999,
    icon: HandPalm,
    accent: "amber",
    bgClass: "from-amber-100 via-yellow-50 to-orange-50",
    iconClass: "text-amber-700",
    chipClass: "bg-amber-200 text-amber-900",
    to: "/palmistry-report",
  },
  {
    key: "face",
    title: "Face Reading Report",
    tagline: "Every Feature Read by AI",
    pages: "15+ pages",
    method: "Samudrik Shastra",
    description:
      "Upload a face photo for a detailed premium reading — face shape, elemental temperament, every feature (forehead, eyes, nose, chin & more), the three zones, life timeline, predictions and remedies.",
    inside: [
      "Face Shape & Elemental Temperament",
      "Feature-by-Feature Analysis (10 features)",
      "Three-Zones Life Timeline & Predictions",
      "Gemstone / Rudraksha / Yantra Remedies",
    ],
    price: 527,
    strike: 999,
    icon: UserFocus,
    accent: "amber",
    bgClass: "from-amber-100 via-yellow-50 to-orange-50",
    iconClass: "text-amber-700",
    chipClass: "bg-amber-200 text-amber-900",
    to: "/face-reading-report",
  },
];

const COMPARE_FEATURES = [
  { label: "Number of Pages", values: ["50+", "30+", "50+", "50+", "25", "25+", "20+", "20+", "15+"] },
  { label: "Astrology Method", values: ["Parashari", "Lal Kitaab", "Nadi (BNN)", "Parashari", "Practical Vedic", "KP + Vastu", "Numerology", "Palmistry", "Samudrik Shastra"] },
  { label: "Personalised for your chart", values: [true, true, true, true, true, true, true, true, true] },
  { label: "Mahadasha Timeline", values: [true, false, false, true, true, false, false, false, false] },
  { label: "Practical Remedies", values: [true, true, true, true, true, true, true, true, true] },
  { label: "Plain-Language Sections", values: [false, true, false, false, true, false, true, true, true] },
  { label: "Activation Timing Map", values: [true, false, true, true, true, false, true, true, true] },
  { label: "PDF + Email + WhatsApp Delivery", values: [true, true, true, true, true, true, true, true, true] },
  { label: "Generation Time", values: ["90 sec", "90 sec", "90 sec", "90 sec", "90 sec", "90 sec", "10 sec", "60 sec", "60 sec"] },
  { label: "Price", values: ["₹527", "₹527", "₹527", "₹527", "₹527", "₹527", "₹527", "₹527", "₹527"] },
];

const PERSONAS = [
  { icon: GraduationCap, title: "Students", desc: "Get clarity on strengths, ideal career path and the timing of academic breakthroughs." },
  { icon: Briefcase, title: "Working professionals", desc: "Decode the right job-vs-business decision, growth windows, and competitive-exam luck." },
  { icon: Heart, title: "Couples", desc: "Compatibility issues, marriage delays, or relationship friction — see the planetary cause and the remedy." },
  { icon: CurrencyInr, title: "Entrepreneurs", desc: "Wealth flow, partnership dynamics, the right launch window and risk-mitigation remedies." },
  { icon: House, title: "Homeowners", desc: "House-buying timing, vastu-defect fixes and energy-mapping for prosperity and peace." },
];

const INSIDE_BLOCKS = [
  { icon: Heart, title: "Love & Relationships", desc: "Emotional patterns, partner traits, marriage timing, friction sources and remedies for harmony." },
  { icon: Briefcase, title: "Career & Profession", desc: "Job vs business, growth phases, government / private fit and success in competitive exams." },
  { icon: CurrencyInr, title: "Money & Finance", desc: "Wealth flow, financial blocks, the hidden luck triggers in your chart and the best windows to invest." },
  { icon: Crown, title: "Wealth & Lifestyle", desc: "Property, vehicles, comforts and the timing of luxurious lifestyle expansions." },
  { icon: ShieldCheck, title: "Health & Wellbeing", desc: "Physical and mental health patterns, vulnerability windows and balancing remedies." },
];

const TESTIMONIALS = [
  { name: "Kajal", text: "Bahut alag tha ye — the way it explained my decision patterns and relationship struggles felt like someone really understood my life. Remedies were simple and doable." },
  { name: "Ankita",text: "What stood out was the depth. Planetary placements, life patterns, timelines — everything connected. Finally got clarity on decisions I was stuck on for months." },
  { name: "Nitya", text: "Tried so many apps, nothing worked. Got my job within 6 months after following the remedies in the report. Hats off!" },
  { name: "Deepak", text: "I've read multiple Kundli reports before, but this one actually explained WHY things were happening. The dasha timing was surprisingly accurate." },
];

const FAQ = [
  { q: "Do I need to log in to generate a report?", a: "No. Every report on this page can be generated by entering your birth details. You can pay as a guest and the PDF is emailed to you immediately, and also sent on WhatsApp." },
  { q: "How is each report personalised?", a: "Every report is computed from YOUR exact date, time, and place of birth using authentic Vedic / KP / Nadi / Numerology methods. The same person never gets the same text — we don't ship templates." },
  { q: "How fast is the delivery?", a: "Most reports generate in 60–90 seconds. The Numerology report finishes in ~10 seconds. The PDF auto-downloads in your browser and lands in your email & WhatsApp in parallel." },
  { q: "Which language is the report in?", a: "Mahakundli and Raj Yog support 5 languages (English, Hindi, Tamil, Telugu, Marathi). Other reports are in English by default with select Hindi sections. Multi-language support is being expanded." },
  { q: "Can I share this link with friends or family?", a: "Yes — every report has its own shareable landing page. Anyone you share it with can read about the report, see what's inside, and generate their own copy without signing up first." },
  { q: "Is my data safe?", a: "Yes. Birth details are used only to compute your chart and are never sold or shared. Payments are handled by Razorpay (PCI-DSS compliant). PDFs stay accessible from your profile if you choose to create one." },
];

export default function ReportsTabSection() {
  const navigate = useNavigate();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="font-[Manrope]" data-testid="reports-tab-section">

      {/* ── A. HERO ─────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#FFFBEB] via-[#FEF3C7]/40 to-[#FFF7ED]">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: "radial-gradient(circle at 12% 20%, #D4AF37 0%, transparent 45%), radial-gradient(circle at 88% 80%, #EA580C 0%, transparent 45%)",
        }} />
        <div className="relative max-w-6xl mx-auto px-4 pt-12 pb-14 md:pt-16 md:pb-20">
          <div className="grid md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-3">
              <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold text-[#8B7322] bg-amber-100 border border-amber-300 px-3 py-1 rounded-full mb-4">
                <Crown size={12} weight="fill" /> Premium Reports · No Sign-up
              </span>
              <h1 className="font-[Cormorant_Garamond,serif] text-4xl sm:text-5xl lg:text-6xl text-[#1E3A5F] leading-[1.05]">
                Accurate Predictions for <span className="text-amber-700">Love, Career &amp; Money</span> — Hidden in Your Janam Kundli.
              </h1>
              <p className="text-base sm:text-lg text-zinc-700 leading-relaxed mt-5 max-w-xl">
                Feeling stuck or lost about your future? Wondering why things don't go right? Pick from 9 premium reports — each one decodes a specific layer of your chart in plain language, with remedies that actually work.
              </p>
              <div className="flex flex-wrap gap-3 mt-7">
                <Button
                  onClick={() => scrollTo("reports-grid")}
                  className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white rounded-full px-7 h-12 font-bold shadow-md text-base"
                  data-testid="reports-hero-cta-pick"
                >
                  Pick Your Report <ArrowRight size={16} weight="bold" className="ml-1" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => scrollTo("reports-compare")}
                  className="rounded-full px-6 h-12 font-bold border-[#1E3A5F]/30 text-[#1E3A5F] hover:bg-[#1E3A5F]/5 text-base"
                  data-testid="reports-hero-cta-compare"
                >
                  Compare All 8
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-7 text-xs text-zinc-600 font-medium">
                <span className="flex items-center gap-1.5"><CheckCircle size={14} weight="fill" className="text-emerald-600" /> 2 lakh+ charts decoded</span>
                <span className="flex items-center gap-1.5"><Clock size={14} weight="fill" className="text-amber-600" /> 60-90 second delivery</span>
                <span className="flex items-center gap-1.5"><FilePdf size={14} weight="fill" className="text-rose-600" /> PDF + Email + WhatsApp</span>
                <span className="flex items-center gap-1.5"><Shield size={14} weight="fill" className="text-blue-600" /> Secure Razorpay payment</span>
              </div>
            </div>
            <div className="md:col-span-2 flex justify-center">
              <div className="relative">
                <div className="absolute -inset-6 bg-gradient-to-br from-amber-300/40 to-orange-400/30 blur-3xl rounded-full" />
                <div className="relative grid grid-cols-2 gap-3 rotate-[-4deg]">
                  {REPORTS.slice(0, 4).map((r, i) => (
                    <div
                      key={r.key}
                      className={`bg-gradient-to-br ${r.bgClass} rounded-2xl border-2 border-white shadow-xl p-4 ${i % 2 ? 'translate-y-6' : ''}`}
                    >
                      <r.icon size={22} weight="duotone" className={r.iconClass} />
                      <p className={`text-[10px] font-bold uppercase tracking-wider mt-2 ${r.iconClass}`}>
                        {r.pages}
                      </p>
                      <p className="text-xs font-bold text-zinc-800 leading-tight mt-1">
                        {r.title}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── B. COMPARISON TABLE ─────────────────────────────── */}
      <section id="reports-compare" className="bg-white py-14 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[10px] uppercase tracking-[0.22em] text-amber-700 font-bold mb-2">Side-by-Side</p>
            <h2 className="font-[Cormorant_Garamond,serif] text-3xl md:text-4xl text-[#1E3A5F]">
              Compare All Nine Premium Reports
            </h2>
            <p className="text-sm text-zinc-600 mt-2">Find the report that answers YOUR question best.</p>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-amber-200/70 shadow-sm">
            <table className="w-full min-w-[980px] text-sm" data-testid="reports-compare-table">
              <thead className="bg-gradient-to-r from-amber-50 to-orange-50 border-b-2 border-amber-200">
                <tr>
                  <th className="text-left p-3 text-[#1E3A5F] font-bold">Feature</th>
                  {REPORTS.map(r => (
                    <th key={r.key} className="p-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <r.icon size={18} weight="duotone" className={r.iconClass} />
                        <span className="text-xs font-bold text-[#1E3A5F] leading-tight">{r.title.replace(' Report', '')}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARE_FEATURES.map((row, idx) => (
                  <tr key={row.label} className={idx % 2 ? "bg-amber-50/30" : "bg-white"}>
                    <td className="p-3 font-semibold text-zinc-700 border-r border-amber-100">{row.label}</td>
                    {row.values.map((v, i) => (
                      <td key={i} className="p-3 text-center text-xs">
                        {typeof v === "boolean" ? (
                          v
                            ? <CheckCircle size={18} weight="fill" className="text-emerald-600 mx-auto" />
                            : <X size={16} weight="bold" className="text-zinc-300 mx-auto" />
                        ) : (
                          <span className={row.label === "Price" ? "font-bold text-amber-700" : "text-zinc-700"}>{v}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className="bg-gradient-to-r from-amber-100 to-orange-100">
                  <td className="p-3" />
                  {REPORTS.map(r => (
                    <td key={r.key} className="p-3 text-center">
                      <Button
                        onClick={() => navigate(r.to)}
                        size="sm"
                        className="bg-[#1E3A5F] hover:bg-[#0F2238] text-white rounded-full text-xs px-4 h-8"
                        data-testid={`compare-cta-${r.key}`}
                      >
                        Open
                      </Button>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── C. 7 REPORT CARDS ───────────────────────────────── */}
      <section id="reports-grid" className="bg-gradient-to-b from-[#FFFBEB] to-white py-14 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[10px] uppercase tracking-[0.22em] text-amber-700 font-bold mb-2">Pick Yours</p>
            <h2 className="font-[Cormorant_Garamond,serif] text-3xl md:text-4xl text-[#1E3A5F]">
              Nine Reports. One Authentic Source.
            </h2>
            <p className="text-sm text-zinc-600 mt-2 max-w-xl mx-auto">Tap any card to open the dedicated report page — see sample pages, what's inside, and generate yours in 90 seconds.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {REPORTS.map(r => <ReportCard key={r.key} report={r} onOpen={() => navigate(r.to)} />)}
          </div>
        </div>
      </section>

      {/* ── D. WHO IS THIS FOR ──────────────────────────────── */}
      <section className="bg-white py-14 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[10px] uppercase tracking-[0.22em] text-amber-700 font-bold mb-2">Built For</p>
            <h2 className="font-[Cormorant_Garamond,serif] text-3xl md:text-4xl text-[#1E3A5F]">
              Who should get a report?
            </h2>
            <p className="text-sm text-zinc-600 mt-2">Anyone facing a dilemma — these reports turn confusion into a clear next step.</p>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
            {PERSONAS.map((p, i) => (
              <div key={p.title} className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200/60 rounded-2xl p-5 text-center hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 mx-auto rounded-full bg-white border-2 border-amber-300 flex items-center justify-center mb-3">
                  <p.icon size={22} weight="duotone" className="text-amber-700" />
                </div>
                <p className="text-[10px] uppercase tracking-wider text-amber-700 font-bold mb-1">{i + 1}</p>
                <h3 className="font-bold text-base text-[#1E3A5F] mb-1.5">{p.title}</h3>
                <p className="text-xs text-zinc-600 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── E. WHAT'S INSIDE ────────────────────────────────── */}
      <section className="bg-gradient-to-br from-[#1E3A5F] to-[#0F2238] py-16 px-4 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[10px] uppercase tracking-[0.22em] text-amber-300 font-bold mb-2">What's Inside</p>
            <h2 className="font-[Cormorant_Garamond,serif] text-3xl md:text-4xl">
              Every report covers the life areas that matter.
            </h2>
            <p className="text-sm text-blue-200 mt-2">Each section is computed from YOUR exact chart — no templates, no generic predictions.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {INSIDE_BLOCKS.map(b => (
              <div key={b.title} className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5 hover:bg-white/8 transition-colors">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mb-3">
                  <b.icon size={22} weight="fill" className="text-white" />
                </div>
                <h3 className="font-bold text-lg text-amber-200 mb-2">{b.title}</h3>
                <p className="text-sm text-blue-100/80 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── F. WHY OCCULTKING ───────────────────────────────── */}
      <section className="bg-white py-14 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[10px] uppercase tracking-[0.22em] text-amber-700 font-bold mb-2">Why Occult King</p>
            <h2 className="font-[Cormorant_Garamond,serif] text-3xl md:text-4xl text-[#1E3A5F]">
              Why thousands trust our reports.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <WhyCard icon={Crown} title="Mentored by Acharya Vikul Bansal" desc="Every report is built on classical methods refined by Acharya Vikul Bansal — Vedic astrologer, KP-method expert and Vastu consultant with 15+ years of practice." />
            <WhyCard icon={Lightning} title="60-90 second delivery" desc="No 2-day wait. Our backend AI pipeline computes your full chart and renders a personalised PDF in under 90 seconds." />
            <WhyCard icon={Globe} title="Email + WhatsApp + Download" desc="Your PDF auto-downloads in the browser, lands in your email inbox, and is delivered on your WhatsApp — all from a single payment." />
            <WhyCard icon={Heart} title="Plain-language sections" desc="No astrology jargon. Every prediction is written in conversational Hindi-English you can actually act on." />
            <WhyCard icon={Shield} title="Secure Razorpay payment" desc="Razorpay handles every payment — PCI-DSS compliant, supports UPI, cards, NetBanking and wallets. Refund within 7 days if you're not happy." />
            <WhyCard icon={Sparkle} title="No login required" desc="Generate as a guest with just your birth details. Optional sign-up later to access your reports vault and earn unified report credits." />
          </div>
        </div>
      </section>

      {/* ── G. HOW IT WORKS ─────────────────────────────────── */}
      <section className="bg-gradient-to-br from-amber-50 to-orange-50 py-14 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[10px] uppercase tracking-[0.22em] text-amber-700 font-bold mb-2">How It Works</p>
            <h2 className="font-[Cormorant_Garamond,serif] text-3xl md:text-4xl text-[#1E3A5F]">
              Three simple steps.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { n: 1, title: "Pick your report", desc: "Open the dedicated page for any of the 7 reports. Read what's inside, see sample pages." },
              { n: 2, title: "Enter your birth details", desc: "Name, date, time, place of birth. ~30 seconds. We compute your exact chart instantly." },
              { n: 3, title: "Pay & receive PDF", desc: "Pay securely via Razorpay. PDF auto-downloads + lands in your email + WhatsApp in 90 sec." },
            ].map(s => (
              <div key={s.n} className="bg-white rounded-2xl border border-amber-200/60 p-6 text-center shadow-sm">
                <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-amber-500 to-orange-500 text-white flex items-center justify-center font-bold text-xl mb-3 shadow-md">
                  {s.n}
                </div>
                <h3 className="font-bold text-lg text-[#1E3A5F] mb-2">{s.title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── H. TESTIMONIALS ─────────────────────────────────── */}
      <section className="bg-white py-14 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[10px] uppercase tracking-[0.22em] text-amber-700 font-bold mb-2">Happy Readers</p>
            <h2 className="font-[Cormorant_Garamond,serif] text-3xl md:text-4xl text-[#1E3A5F]">
              We've helped thousands of people.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {TESTIMONIALS.map(t => (
              <div key={t.name} className="bg-gradient-to-br from-amber-50/50 to-white border border-amber-200/40 rounded-2xl p-5 text-sm leading-relaxed text-zinc-700">
                <div className="flex items-center gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map(i => <Star key={i} size={14} weight="fill" className="text-amber-500" />)}
                </div>
                <p className="italic mb-3">"{t.text}"</p>
                <p className="text-xs font-bold text-[#1E3A5F]">— {t.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── I. FAQ ──────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-[#FFFBEB] to-white py-14 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[10px] uppercase tracking-[0.22em] text-amber-700 font-bold mb-2">FAQ</p>
            <h2 className="font-[Cormorant_Garamond,serif] text-3xl md:text-4xl text-[#1E3A5F]">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-2">
            {FAQ.map((f, idx) => <FaqRow key={idx} item={f} />)}
          </div>
          <div className="mt-10 text-center bg-white rounded-2xl border border-amber-200 p-6">
            <p className="text-sm text-zinc-700 mb-3">Still have a question?</p>
            <div className="flex flex-wrap justify-center gap-3">
              <a href="https://wa.me/919093046046" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-full px-5 h-10 text-sm font-bold transition-colors"
                data-testid="reports-faq-whatsapp">
                <Phone size={14} weight="fill" /> WhatsApp +91 90930 46046
              </a>
              <a href="mailto:support@occultking.com"
                className="inline-flex items-center gap-2 bg-[#1E3A5F] hover:bg-[#0F2238] text-white rounded-full px-5 h-10 text-sm font-bold transition-colors"
                data-testid="reports-faq-email">
                <Envelope size={14} weight="fill" /> support@occultking.com
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────
function ReportCard({ report, onOpen }) {
  const r = report;
  return (
    <div
      className={`relative rounded-2xl border-2 border-white bg-gradient-to-br ${r.bgClass} p-6 flex flex-col shadow-md hover:shadow-2xl transition-shadow`}
      data-testid={`report-card-${r.key}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-12 h-12 rounded-xl bg-white shadow flex items-center justify-center`}>
          <r.icon size={26} weight="duotone" className={r.iconClass} />
        </div>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${r.chipClass}`}>
          {r.pages}
        </span>
      </div>
      <h3 className="font-[Cormorant_Garamond,serif] text-2xl text-[#1E3A5F] leading-tight">{r.title}</h3>
      <p className={`text-[11px] font-bold uppercase tracking-wider mt-1 ${r.iconClass}`}>
        {r.method}
      </p>
      <p className="text-sm text-zinc-700 mt-3 leading-relaxed">{r.description}</p>
      <ul className="mt-4 space-y-1.5 text-xs text-zinc-700">
        {r.inside.map(p => (
          <li key={p} className="flex items-start gap-2">
            <CheckCircle size={14} weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" />
            <span>{p}</span>
          </li>
        ))}
      </ul>
      <div className="mt-5 pt-4 border-t border-white/60 flex items-end justify-between">
        <div>
          <span className="text-xs text-zinc-500 line-through mr-1">₹{r.strike}</span>
          <span className="text-2xl font-bold text-[#1E3A5F]">₹{r.price}</span>
        </div>
        <Button
          onClick={onOpen}
          className="bg-[#1E3A5F] hover:bg-[#0F2238] text-white rounded-full text-sm px-5 h-10 font-bold shadow"
          data-testid={`report-card-cta-${r.key}`}
        >
          Open <ArrowRight size={14} className="ml-1" weight="bold" />
        </Button>
      </div>
    </div>
  );
}

function WhyCard({ icon: Icon, title, desc }) {
  return (
    <div className="bg-gradient-to-br from-white to-amber-50/30 border border-amber-200/50 rounded-2xl p-5 hover:shadow-lg transition-shadow">
      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mb-3">
        <Icon size={20} weight="fill" className="text-white" />
      </div>
      <h3 className="font-bold text-base text-[#1E3A5F] mb-1.5">{title}</h3>
      <p className="text-sm text-zinc-600 leading-relaxed">{desc}</p>
    </div>
  );
}

function FaqRow({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-amber-200/60 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full text-left p-4 flex items-center justify-between gap-3 hover:bg-amber-50/40 transition-colors"
        data-testid="reports-faq-toggle"
      >
        <span className="font-semibold text-[#1E3A5F] text-sm">{item.q}</span>
        <CaretDown size={16} weight="bold" className={`text-amber-700 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 text-sm text-zinc-700 leading-relaxed border-t border-amber-100 pt-3">
          {item.a}
        </div>
      )}
    </div>
  );
}
